import React, { useEffect, useState } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import api, { API, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { MessagesSquare, Send, FileDown, Copy, ExternalLink } from "lucide-react";

const TYPE_BADGE = {
    client: "bg-blue-100 text-blue-800",
    internal: "bg-slate-200 text-slate-700",
    review: "bg-purple-100 text-purple-800",
};
const TYPE_LABEL = { client: "Client", internal: "Internal", review: "Review" };

export default function QueriesOverview() {
    const { engagementId } = useParams();
    const ctx = useOutletContext();
    const canEdit = !!ctx?.canEdit;
    const [queries, setQueries] = useState([]);
    const [typeF, setTypeF] = useState("all");
    const [statusF, setStatusF] = useState("open");
    const [sel, setSel] = useState({});
    const token = localStorage.getItem("qube_token");

    const load = () => api.get(`/engagements/${engagementId}/queries`).then((r) => setQueries(r.data)).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
    useEffect(() => { load(); setSel({}); }, [engagementId]);

    const filtered = queries.filter((q) => (typeF === "all" || q.type === typeF) && (statusF === "all" || q.status === statusF));
    const drafts = queries.filter((q) => q.type === "client" && !q.released_at && q.status === "open");
    const selectedIds = Object.keys(sel).filter((k) => sel[k]);
    const openClient = queries.filter((q) => q.type === "client" && q.status === "open");

    const release = async (ids) => {
        if (!window.confirm(ids ? `Send ${ids.length} selected client quer${ids.length === 1 ? "y" : "ies"} to the client portal now?` : `Send ALL ${drafts.length} draft client queries to the client portal now?`)) return;
        try {
            const { data } = await api.post(`/engagements/${engagementId}/queries/release`, ids ? { query_ids: ids } : {});
            toast.success(`${data.released} quer${data.released === 1 ? "y" : "ies"} sent to the client portal`);
            setSel({}); load(); ctx?.reload?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const copyForEmail = () => {
        if (!openClient.length) return toast.error("No open client queries to copy");
        const text = openClient.map((q, i) => `${i + 1}. ${q.title}${q.wp_code ? ` [${q.wp_code}]` : ""}${q.text ? `\n   ${q.text}` : ""}`).join("\n\n");
        navigator.clipboard.writeText(text).then(() => toast.success("Client queries copied — paste into your email")).catch(() => toast.error("Copy failed"));
    };

    const exportHref = `${API}/engagements/${engagementId}/client-queries.docx?auth=${encodeURIComponent(token || "")}`;
    const Pill = ({ active, onClick, children, tid }) => (
        <button data-testid={tid} onClick={onClick} className={`text-xs px-2.5 py-1 rounded-sm border ${active ? "border-brand bg-brand text-white" : "border-border hover:bg-slate-50"}`}>{children}</button>
    );

    return (
        <div className="p-6 overflow-auto h-full" data-testid="queries-overview">
            <div className="flex items-center gap-2 mb-4">
                <MessagesSquare className="w-5 h-5 text-brand" />
                <h1 className="font-display font-bold text-xl">Queries</h1>
                <span className="text-sm text-muted-foreground">({filtered.length} shown)</span>
            </div>

            {/* Client-query actions */}
            <div className="bg-white border border-border rounded-sm p-3 mb-4" data-testid="client-query-actions">
                <div className="text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground mb-2">Client queries — you choose when to send</div>
                <p className="text-xs text-muted-foreground mb-3">Client queries stay <b>private drafts</b> until you release them — the client sees nothing and gets no notification until you send. {drafts.length} draft{drafts.length === 1 ? "" : "s"} not yet sent.</p>
                <div className="flex flex-wrap items-center gap-2">
                    <button data-testid="release-selected-btn" disabled={!canEdit || !selectedIds.length} onClick={() => release(selectedIds)} className="flex items-center gap-1.5 text-xs bg-brand hover:bg-brand-hover text-white px-3 py-1.5 rounded-sm disabled:opacity-40"><Send className="w-3.5 h-3.5" /> Send selected ({selectedIds.length})</button>
                    <button data-testid="release-all-btn" disabled={!canEdit || !drafts.length} onClick={() => release(null)} className="flex items-center gap-1.5 text-xs border border-brand text-brand hover:bg-brand/5 px-3 py-1.5 rounded-sm disabled:opacity-40"><Send className="w-3.5 h-3.5" /> Send all drafts</button>
                    <button data-testid="copy-queries-btn" onClick={copyForEmail} className="flex items-center gap-1.5 text-xs border border-border hover:bg-slate-50 px-3 py-1.5 rounded-sm"><Copy className="w-3.5 h-3.5" /> Copy for email</button>
                    <a data-testid="export-queries-btn" href={exportHref} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs border border-border hover:bg-slate-50 px-3 py-1.5 rounded-sm"><FileDown className="w-3.5 h-3.5" /> Export (.docx)</a>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[11px] text-muted-foreground">Type:</span>
                {["all", "client", "internal", "review"].map((t) => <Pill key={t} tid={`filter-type-${t}`} active={typeF === t} onClick={() => setTypeF(t)}>{t === "all" ? "All" : TYPE_LABEL[t]}</Pill>)}
                <span className="text-[11px] text-muted-foreground ml-3">Status:</span>
                {["open", "closed", "all"].map((s) => <Pill key={s} tid={`filter-status-${s}`} active={statusF === s} onClick={() => setStatusF(s)}>{s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}</Pill>)}
            </div>

            {/* List */}
            <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="queries-list">
                {filtered.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No queries match these filters.</div>}
                {filtered.map((q) => {
                    const isDraft = q.type === "client" && !q.released_at;
                    return (
                        <div key={q.id} className="flex items-start gap-3 px-4 py-2.5" data-testid={`query-row-${q.id}`}>
                            {isDraft && q.status === "open" && canEdit ? (
                                <input type="checkbox" data-testid={`query-select-${q.id}`} checked={!!sel[q.id]} onChange={(e) => setSel((s) => ({ ...s, [q.id]: e.target.checked }))} className="mt-1" />
                            ) : <span className="w-3.5" />}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold ${TYPE_BADGE[q.type]}`}>{TYPE_LABEL[q.type]}</span>
                                    <span className="text-sm font-medium truncate">{q.title}</span>
                                    {q.status === "closed" && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500">Closed</span>}
                                    {q.type === "client" && (isDraft
                                        ? <span data-testid={`query-draft-${q.id}`} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 font-semibold">Draft — not sent</span>
                                        : <span data-testid={`query-sent-${q.id}`} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 font-semibold">Sent to client</span>)}
                                    {q.type === "client" && !isDraft && q.client_answered && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">Client replied</span>}
                                </div>
                                {q.text && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{q.text}</div>}
                                <div className="text-[11px] text-muted-foreground mt-0.5">Raised by {q.raised_by?.user_name || "—"}{q.released_by ? ` · sent by ${q.released_by}` : ""}</div>
                            </div>
                            {q.wp_id && (
                                <Link to={`/engagements/${engagementId}/wp/${q.wp_id}`} data-testid={`query-wp-link-${q.id}`} className="shrink-0 flex items-center gap-1 text-xs text-brand hover:underline mt-0.5" title="Open the working paper">
                                    {q.wp_code || "WP"} <ExternalLink className="w-3 h-3" />
                                </Link>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
